import { sessionFetch, recoverSession, SessionEndedError } from "@/lib/session";
import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "@/lib/api/poseidon.d";
import { AuthError, APIError, NetworkError, readableErrorMessage } from "@/lib/errors";
import { apiBase } from "@/lib/apiBase";

/**
 * A response/DTO schema by name.
 *
 * openapi-fetch returns response bodies through its internal `Readable<…>`
 * mapped type, which re-expands branded id aliases (`ModelId<'faq'>`, emitted as
 * `string & { [__brand]?: … }`) into a structurally-equal but nominally-distinct
 * object shape. A schema type taken straight from `components["schemas"]`
 * therefore is *not* directly assignable to what `apiClient` hands back, so
 * hooks cast the client result to the relevant `SchemaType` (or via `pagedItems`
 * for lists) at the fetch boundary. `SchemaType` is the consumer-facing contract
 * where branded ids stay assignable to plain `string`.
 */
export type SchemaType<Name extends keyof components["schemas"]> = components["schemas"][Name];

/**
 * Rewrite same-origin API paths to the runtime API origin.
 *
 * The client is created with an empty `baseUrl` (the runtime origin from
 * `/config.json` isn't known at import time), so openapi-fetch resolves `/v1/*`
 * and `/auth/*` requests against the document origin. At request time — after
 * `loadConfig()` has resolved — this prepends `apiBase()` so the browser hits
 * the real API cross-origin (+ CORS). When `apiBase()` is empty (local dev with
 * the Vite proxy) requests stay same-origin relative. Absolute URLs to other
 * origins are left untouched. Registered first so it runs before the others.
 */
const baseUrlMiddleware: Middleware = {
  async onRequest({ request }) {
    const base = apiBase();
    if (!base || typeof window === "undefined") {
      return undefined;
    }
    const url = new URL(request.url);
    if (url.origin === window.location.origin && /^\/(v\d+|auth)\//.test(url.pathname)) {
      return new Request(`${base}${url.pathname}${url.search}`, request);
    }
    return undefined;
  },
};

/**
 * Middleware to attach X-Role header from localStorage (client-side only)
 */
const roleMiddleware: Middleware = {
  async onRequest({ request }) {
    const role = typeof localStorage !== "undefined" ? localStorage.getItem("role") : null;
    if (role) {
      request.headers.set("X-Role", role);
    }
    return request;
  },
};

/** Compatibility for callers that request an explicit session check. */
export async function refreshOnce(): Promise<{ ok: boolean; retryable: boolean }> {
  const outcome = await recoverSession();
  return { ok: outcome === "authenticated", retryable: outcome === "temporarily-unavailable" };
}

/**
 * Middleware to translate HTTP errors into typed error classes
 */
const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return undefined;

    let errorResponse: { message?: string; error?: string } | undefined;
    try {
      errorResponse = await response.clone().json();
    } catch {
      errorResponse = {
        error: `${response.status} ${response.statusText || "Request failed"}`,
      };
    }

    const errorMessage = readableErrorMessage(
      errorResponse?.message ?? errorResponse?.error ?? "Something went wrong",
    );

    throw new APIError(errorMessage, response.status);
  },
  async onError({ error }) {
    if (error instanceof SessionEndedError) throw new AuthError(error.message);
    if (error instanceof AuthError || error instanceof APIError || error instanceof NetworkError) {
      throw error;
    }
    if (
      error instanceof TypeError &&
      typeof error.message === "string" &&
      error.message.includes("fetch")
    ) {
      throw new NetworkError("Network request failed. Please check your connection.");
    }
    throw error;
  },
};

const apiClient = createClient<paths>({
  // Empty at creation — the runtime API origin isn't known until `/config.json`
  // loads. `baseUrlMiddleware` prepends `apiBase()` per request instead.
  baseUrl: "",
  fetch: sessionFetch,
  credentials: "include",
  headers: { Accept: "application/json" },
  querySerializer: {
    array: { style: "form", explode: false },
  },
});

apiClient.use(baseUrlMiddleware);
apiClient.use(roleMiddleware);
apiClient.use(errorMiddleware);

export default apiClient;
export type { paths } from "@/lib/api/poseidon.d";

/**
 * Returns the success body of an `apiClient` result, asserting it is present.
 *
 * `errorMiddleware` throws on every non-2xx response, so any result reaching
 * here is a 2xx whose body is the typed success shape — errors travel via HTTP
 * status, never a `200 + { error }` envelope. openapi-fetch still types `data`
 * as `T | undefined` (a 2xx body may be absent), so this narrows it once at the
 * call site. Do not use on endpoints that legitimately return no body (e.g. 204
 * deletes); read `.data` directly there.
 */
export function unwrap<T>(result: { data?: T }): T {
  if (result.data == null) {
    throw new APIError("Empty response from server", 500);
  }
  return result.data;
}

/**
 * Extracts the `items` array from a paginated (`OffsetPaginatedResponseDto`)
 * response body.
 *
 * The generated `OffsetPaginatedResponseDto` declares `items: Record<string,
 * never>[]`; each concrete list endpoint then intersects that base with `{
 * items?: SpecificDto[] }`. The intersection leaves `items`' element type as
 * `Record<string, never> & SpecificDto`, which is not assignable to the clean
 * `SpecificDto`. This helper narrows it back to the concrete element type once,
 * so callers don't repeat the cast.
 */
export function pagedItems<T>(body: { items?: readonly unknown[] } | null | undefined): T[] {
  return (body?.items ?? []) as T[];
}
